import { Component, Input, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, timer } from 'rxjs';
import { exhaustMap } from 'rxjs/operators';
import { PosTransaction } from 'src/app/models/pos/pos-device';
import { PosTransactionStatus, PosTransactionType, posTransactionStatusColor, posTransactionStatusLabel } from 'src/app/models/enums/pos-enums';
import { PosTransactionService } from 'src/app/services/pos-device.service';
import { PosHubService } from 'src/app/services/pos-hub.service';
import { extractErrorMessage } from '../../../accounting/shared/e-invoice.helpers';

type Step = 'confirm' | 'waiting' | 'result';

/** Cihaz sonucu bu aralıkla yoklanır (hızlı satıştaki gönderim penceresiyle aynı). */
const POLL_MS = 2000;

/**
 * Onaylı satışın cihazdan iptali (karta iade). İstek cihaza gider, sonuç yoklanır ve pencere
 * sonucu gösterir. İptal onaylanınca sunucu satışa bağlı ödemenin iadesini POS kasasından
 * çıkar, gerçekleşmeyen komisyonu kasaya gelir yazar ve müşteriyi müşteriye yansıtılan
 * komisyon hariç borçlandırır. Pencere beklerken kapatılırsa işlem açık kalır; sonuç
 * POS İşlemleri ekranında ve arka plan mutabakatında izlenir.
 */
@Component({
  selector: 'app-pos-void-modal',
  templateUrl: './pos-void-modal.component.html',
})
export class PosVoidModalComponent implements OnDestroy {
  /** İptal edilecek onaylı satış. */
  @Input() sale!: PosTransaction;

  readonly Status = PosTransactionStatus;
  readonly statusColor = posTransactionStatusColor;

  step: Step = 'confirm';
  sending = false;
  cancelling = false;
  error: string | null = null;
  pollError: string | null = null;
  transaction: PosTransaction | null = null;
  private poll?: Subscription;

  constructor(
    public activeModal: NgbActiveModal,
    private service: PosTransactionService,
    private posHub: PosHubService,
  ) {}

  ngOnDestroy(): void {
    this.stopPolling();
    // Sonuç pencerede görünüyordu; pencere kapandıysa genel POS bildirimi gelir.
    if (this.transaction) this.posHub.unwatch(this.transaction.id);
  }

  get isLinked(): boolean {
    return !!(this.sale.salesId || this.sale.salesGroupId);
  }

  statusLabel(status: PosTransactionStatus): string {
    return posTransactionStatusLabel(PosTransactionType.Void, status);
  }

  send(): void {
    this.sending = true;
    this.error = null;
    this.service.void(this.sale.id).subscribe({
      next: res => {
        this.sending = false;
        this.transaction = res.data ?? null;
        if (this.transaction) this.posHub.watch(this.transaction.id);
        this.step = 'waiting';
        this.onTransaction();
      },
      error: error => {
        this.sending = false;
        this.error = extractErrorMessage(error, 'İptal isteği cihaza gönderilemedi.');
      },
    });
  }

  private startPolling(): void {
    if (this.poll && !this.poll.closed) return;
    // exhaustMap: yavaş cevapta istekler üst üste binmez.
    this.poll = timer(POLL_MS, POLL_MS).pipe(
      exhaustMap(() => this.service.refresh(this.transaction!.id)),
    ).subscribe({
      next: res => {
        this.pollError = null;
        this.transaction = res.data ?? this.transaction;
        this.onTransaction();
      },
      error: error => {
        this.pollError = extractErrorMessage(error, 'Cihaz durumu alınamadı.');
        this.stopPolling();
      },
    });
  }

  private onTransaction(): void {
    const t = this.transaction;
    if (!t) return;
    if (!t.isFinal) {
      this.startPolling();
      return;
    }
    this.stopPolling();
    this.step = 'result';
  }

  checkNow(): void {
    this.pollError = null;
    this.startPolling();
  }

  /** Cihazda bekleyen iptal isteğini geri çeker; teyit gelene kadar yoklama sürer. */
  cancelOnDevice(): void {
    if (!this.transaction) return;
    this.cancelling = true;
    this.service.cancel(this.transaction.id).subscribe({
      next: res => {
        this.cancelling = false;
        this.transaction = res.data ?? this.transaction;
        this.onTransaction();
      },
      error: error => {
        this.cancelling = false;
        this.pollError = extractErrorMessage(error, 'Geri çekme isteği gönderilemedi.');
      },
    });
  }

  get waitingText(): string {
    switch (this.transaction?.status) {
      case PosTransactionStatus.Pending: return 'İptal isteği cihaza iletiliyor…';
      case PosTransactionStatus.InProgress: return 'Cihazda — iptalin cihazdan onaylanması bekleniyor.';
      case PosTransactionStatus.Suspended: return 'İşlem cihazda askıya alındı. Cihazdan devam ettirin ya da geri çekin.';
      default: return 'Sonuç bekleniyor…';
    }
  }

  get approved(): boolean {
    return this.transaction?.status === PosTransactionStatus.Approved;
  }

  /** Değişiklik olduysa (istek gönderildiyse) liste yenilensin diye işlem döner. */
  close(): void {
    this.stopPolling();
    if (this.transaction) this.activeModal.close(this.transaction);
    else this.activeModal.dismiss();
  }

  private stopPolling(): void {
    this.poll?.unsubscribe();
    this.poll = undefined;
  }
}
